import { ArrowRight, Truck } from "lucide-react";
import type { ComponentProps, ReactElement, ReactNode } from "react";
import { useState } from "react";
import { useWatch } from "react-hook-form";

import type { ServiceEntity } from "~/entities/service";
import { groupSelectedServices } from "~/entities/service";
import type { CalculatorFormSchema } from "~/features/service";
import { CalculatorForm, useCalculatorForm } from "~/features/service";
import { sum } from "~/shared/arr";
import { formatCurrency, PartOfSpeech, Tk, useTranslation2 } from "~/shared/i18n";
import { COLON, EMPTY } from "~/shared/str";
import { cn } from "~/shared/ui";
import { Button } from "~/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/shared/ui/card";

type Props = ComponentProps<"div"> &
  Pick<LeftCardProps, "services" | "moreInfoButton"> &
  Pick<RightCardProps, "buttonToOrder">;

function CalculatorCard({ services, moreInfoButton, buttonToOrder, className, ...props }: Props) {
  const {
    translation: { t },
  } = useTranslation2();

  const [orderedServices] = useState([]);
  const [, form] = useCalculatorForm(orderedServices, services, t);

  return (
    <div className={cn("flex flex-col md:flex-row *:md:w-1/2", className)} {...props}>
      <LeftCard
        form={form}
        orderedServices={orderedServices}
        services={services}
        moreInfoButton={moreInfoButton}
        className="max-h-192 overflow-auto"
      />
      <RightCard form={form} services={services} buttonToOrder={buttonToOrder} />
    </div>
  );
}

type Schema = CalculatorFormSchema;

type LeftCardProps = Omit<ComponentProps<typeof Card>, "onSubmit"> & {
  form: NonNullable<ComponentProps<typeof CalculatorForm>["form"]>;
  orderedServices: NonNullable<ComponentProps<typeof CalculatorForm>["orderedServices"]>;
  services: ServiceEntity.Schema[];
  moreInfoButton: (service: ServiceEntity.Schema) => ReactNode;
};

function LeftCard({
  form,
  orderedServices,
  services,
  moreInfoButton,
  className,
  ...props
}: LeftCardProps) {
  const {
    postproc: { tc },
  } = useTranslation2();

  return (
    <Card
      className={cn("border-0 shadow-none max-md:rounded-b-none md:rounded-r-none", className)}
      {...props}
    >
      <CardHeader>
        <CardTitle>{[tc(Tk.SELECT_SERVICES), COLON].join(EMPTY)}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <CalculatorForm
          form={form}
          orderedServices={orderedServices}
          services={services}
          moreInfoButton={moreInfoButton}
        />
        {[
          {
            title: Tk.VEHICLE,
            content: (
              <div className="flex flex-col items-center">
                <Truck />
                {tc(Tk.TRUCK)}
              </div>
            ),
          },
          {
            title: Tk.NUMBER_OF_LOADERS,
            content: <span>2</span>,
          },
        ].map(({ title, content }, index) => (
          <div key={index} className="flex items-center gap-4">
            <span>{[tc(title), COLON].join(EMPTY)}</span>
            {content}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

type RightCardProps = ComponentProps<typeof Card> &
  Pick<LeftCardProps, "services" | "form"> & {
    buttonToOrder?:
      | ((
          button: ReactElement<ComponentProps<typeof Button>>,
          services: ComponentProps<typeof LeftCard>["services"],
          selectedServices: Schema,
        ) => ReactNode)
      | undefined;
  };

function RightCard({ form, services, buttonToOrder, className, ...props }: RightCardProps) {
  const {
    postproc: { tc },
  } = useTranslation2();

  useWatch({
    control: form.control,
  });

  const groupedSelectedServices = Object.values(
    groupSelectedServices(services, Object.values(form.getValues()).flat()),
  )
    .flat()
    .filter((value) => !!value);

  const costOfServices = Object.values(form.getValues()).reduce(
    (totalCost, servicesByCategory) =>
      totalCost + calculateCostOfServices(servicesByCategory, services),
    0,
  );

  const buttonToOrder_ = buttonToOrder ?? <ButtonToOrder />;

  return (
    <Card
      className={cn(
        "from-primary-lighter to-primary text-primary-foreground border-0 bg-linear-to-br shadow-none max-md:rounded-t-none md:rounded-l-none",
      )}
      {...props}
    >
      <CardHeader>
        <CardTitle>{[tc(Tk.YOUR_ORDER), COLON].join(EMPTY)}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="rounded-lg bg-white/20 p-4">
          <ul className="flex flex-col gap-2">
            {groupedSelectedServices.map(({ name, price, count }, index) => (
              <li key={index} className="flex justify-between">
                <span>
                  {name} ({count})
                </span>
                <span>{formatCurrency(price * count)}</span>
              </li>
            ))}
          </ul>
          <hr className="my-3 h-px border-0 bg-white/30" />
          <div className="flex justify-between font-bold">
            <span className="text-lg">{[tc(Tk.TOTAL), COLON].join(EMPTY)}</span>
            <span className="text-xl">{formatCurrency(costOfServices)}</span>
          </div>
        </div>
        {typeof buttonToOrder_ === "function"
          ? buttonToOrder_(<ButtonToOrder />, services, form.getValues())
          : buttonToOrder_}
      </CardContent>
    </Card>
  );
}

type ButtonToOrderProps = ComponentProps<typeof Button>;

function ButtonToOrder({
  variant = "secondary",
  size = "xl",
  className,
  ...props
}: ButtonToOrderProps) {
  const {
    postproc: { tc },
  } = useTranslation2();

  return (
    <Button
      variant={variant}
      size={size}
      className={cn("text-primary bg-primary-foreground hover:bg-primary-foreground/90", className)}
      {...props}
    >
      {tc(Tk.ORDER, {
        context: PartOfSpeech.verb,
      })}
      <ArrowRight />
    </Button>
  );
}

function calculateCostOfServices(formServices: Schema[keyof Schema], services: Props["services"]) {
  return sum(
    formServices.map(
      ({ id, count }) => (services.find(({ id: id_ }) => id_ === id)?.price ?? 0) * count,
    ),
  );
}

export { CalculatorCard };
